import { Model, Factory, belongsTo, Response } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randNumber, randParagraph } from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MockServer, MirageServer } from './server.js'
import { createErrorResponse, pageable, sortable } from './utils.js'
import { getPeriod } from '@/utils/date/getter.js'

export type DayNoteDetailData = SchemaAttributes<'DayNote'>

let uniqueDates: string[]
const generateDateArray = (numOfDates: number) => {
  const period = getPeriod(new Date(), 'month')
  const dateArray = []

  const currentDate = new Date(period.from)
  while (currentDate <= period.to) {
    dateArray.push(currentDate.toISOString().split('T')[0])
    currentDate.setDate(currentDate.getDate() + 1)
  }

  const totalDates = dateArray.length
  const numToRemove = totalDates - numOfDates

  // Create a set of unique random indexes to remove
  const indexesToRemove = new Set()
  while (indexesToRemove.size < numToRemove) {
    const randomIndex = randNumber({ min: 0, max: totalDates - 1 })
    indexesToRemove.add(randomIndex)
  }

  // Remove the dates at the specified indexes
  const filteredDateArray = dateArray.filter(
    (_, index) => !indexesToRemove.has(index),
  )

  return filteredDateArray
}

export const model = (Model as ModelDefinition<DayNoteDetailData>).extend({
  department: belongsTo('department'),
})

export const factory = Factory.extend<DayNoteDetailData>({
  date(index) {
    return uniqueDates[index]
  },
  text() {
    return randParagraph({ maxCharCount: 200 })
  },
})

export function routes(server: MockServer) {
  server.resource('dayNote', { path: '/day_notes' })

  server.post('/day_notes', function (db, request) {
    const requestBody = JSON.parse(request.requestBody)
    const attributes = requestBody.data.attributes
    const existingPost = db.findBy('dayNote', { date: attributes.date })

    if (existingPost) {
      return new Response(
        400,
        {},
        {
          errors: [
            { detail: 'The fields department, date must make a unique set.' },
          ],
        },
      )
    }
    return db.create('dayNote', attributes)
  })

  server.get('/day_notes', function (this: MirageServer, schema, request) {
    const dayNotes = schema.all('dayNote')

    // Apply sorting if requested
    const sorted = sortable(dayNotes, request)

    // Apply pagination if requested
    const { data, meta } = pageable(sorted, request)

    // Return serialized response with pagination meta
    return {
      ...this.serialize(data),
      meta: {
        pagination: meta,
      },
    }
  })

  server.patch('/day_notes/:id', function (db, request) {
    const id = request.params.id
    const requestBody = JSON.parse(request.requestBody)
    const attributes = requestBody.data.attributes
    const existingDayNoteByDate = db.findBy('dayNote', {
      date: attributes.date,
    })

    if (existingDayNoteByDate && existingDayNoteByDate.id !== id) {
      return new Response(
        400,
        {},
        {
          errors: [
            { detail: 'The fields department, date must make a unique set.' },
          ],
        },
      )
    }

    const dayNote = db.find('dayNote', id)

    if (!dayNote) {
      return new Response(
        404,
        { 'Content-Type': 'application/json' },
        { errors: [{ detail: 'Day note not found.' }] },
      )
    }

    dayNote.update(attributes)
    return db.all('dayNote')
  })

  server.delete('/day_notes/:id', function (db, request) {
    const id = request.params.id
    const dayNote = db.find('dayNote', id)

    if (!dayNote) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Day Note with id ${id} not found` }],
      })
    }

    server.db.dayNotes.remove(dayNote)
    return dayNote
  })
}

export function seeds(server: MockServer) {
  const numberOfItems = 15
  /**
   * Create list of several dayNote's, guaranteeing that all of them have unique dates.
   */
  uniqueDates = generateDateArray(numberOfItems)
  server.createList('dayNote', numberOfItems)
}
